import React from "react";

import Header from "../components/Header";
import Footer from "../components/Footer";

export default function PrivacyPolicy() {
  return (
    <>
      <Header />

      <div className="max-w-4xl mx-auto px-6 py-16 text-gray-800">
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">
        Last updated: January 2026
      </p>

      <p className="mb-6">
        GymPlix ("we", "our", or "us") operates the GymPlix platform, accessible at{" "}
        <a
          href="https://app.gymplix.com"
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://app.gymplix.com
        </a>.
        <br />
        <strong>GymPlix is operated by Creative Brains LTD.</strong>
      </p>

      <p className="mb-6">
        This Privacy Policy explains how we collect, use, store, and protect personal
        data when you use our services, including integrations with third-party
        platforms such as Meta Platforms (Facebook).
      </p>

      {/* 1 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        1. Scope of This Policy
      </h2>

      <p className="mb-4">This Privacy Policy applies to:</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Visitors of our public website</li>
        <li>Registered users of the GymPlix platform</li>
        <li>
          Users who connect third-party services (e.g. Meta Platforms (Facebook), Pinterest, websites)
        </li>
      </ul>

      <p className="mb-6">
        By using GymPlix, you agree to the collection and use of information as
        described in this policy.
      </p>

      {/* 2 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        2. Information We Collect
      </h2>

      <h3 className="font-semibold mt-6 mb-2">
        2.1 Information You Provide Directly
      </h3>

      <p className="mb-2">We may collect:</p>

      <ul className="list-disc pl-6 mb-6">
        <li>Name or username</li>
        <li>Email address</li>
        <li>Account credentials (encrypted)</li>
        <li>Website URLs you choose to connect</li>
        <li>
          Content metadata you submit (titles, descriptions, images)
        </li>
      </ul>

      <h3 className="font-semibold mt-6 mb-2">
        2.2 Information from Connected Services
      </h3>

      <p className="mb-2">
        When you connect third-party services (such as Meta Platforms (Facebook) or Pinterest), we may access:
      </p>

      <ul className="list-disc pl-6 mb-4">
        <li>Account identifiers</li>
        <li>Platform-specific data (boards, pages, profiles)</li>
        <li>Publishing permissions</li>
      </ul>

      <p className="mb-6">
        <strong>GymPlix only accesses data explicitly authorized by the user.</strong>
        All access requires your direct consent through the platform's authorization flow.
      </p>

      <h3 className="font-semibold mt-6 mb-2">
        2.3 Automatically Collected Information
      </h3>

      <p className="mb-2">
        We may collect limited technical data, including:
      </p>

      <ul className="list-disc pl-6 mb-4">
        <li>IP address</li>
        <li>Browser type</li>
        <li>Device information</li>
        <li>Log timestamps</li>
      </ul>

      <p className="mb-6">
        This data is used solely for security, performance, and diagnostics.
      </p>

      {/* 3 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        3. How We Use Your Information
      </h2>

      <p className="mb-2">We use collected information to:</p>

      <ul className="list-disc pl-6 mb-6">
        <li>Provide access to the GymPlix platform</li>
        <li>Enable content preparation and publishing features</li>
        <li>Connect and manage authorized third-party integrations</li>
        <li>Store user-generated content in a secure library</li>
        <li>Maintain platform security and reliability</li>
        <li>Respond to user inquiries and support requests</li>
      </ul>

      <p className="mb-6">We do not sell user data.</p>

      {/* 4 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        4. Meta Platforms (Facebook) API Usage
      </h2>

      <p className="mb-4">
        GymPlix integrates with Meta Platforms (Facebook) through the official Facebook API.
      </p>

      <p className="mb-2">Meta Platforms (Facebook) data is used only to:</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Authenticate users via Facebook Login</li>
        <li>Access Facebook Pages you authorize</li>
        <li>Read and publish content on your behalf (with explicit permission)</li>
        <li>Display content inside the GymPlix interface</li>
      </ul>

      <p className="mb-2">All Meta Platforms (Facebook) actions:</p>

      <ul className="list-disc pl-6 mb-6">
        <li><strong>Require explicit user authorization</strong></li>
        <li>Are initiated by the user</li>
        <li>
          Can be revoked at any time via Facebook or GymPlix settings
        </li>
      </ul>

      <p className="mb-6">
        GymPlix complies with Meta's Platform Policies and developer requirements.
        We do not use Facebook data for advertising purposes or sharing with third parties.
      </p>

      {/* 5 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        5. Pinterest API Usage
      </h2>

      <p className="mb-4">
        GymPlix integrates with Pinterest through the official Pinterest API.
      </p>

      <p className="mb-2">Pinterest data is used only to:</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Create and publish Pins on behalf of the user</li>
        <li>Read boards and related metadata required for publishing</li>
        <li>Display content inside the GymPlix interface</li>
      </ul>

      <p className="mb-2">All Pinterest actions:</p>

      <ul className="list-disc pl-6 mb-6">
        <li>Require explicit user authorization</li>
        <li>Are initiated by the user</li>
        <li>
          Can be revoked at any time via Pinterest or GymPlix settings
        </li>
      </ul>

      <p className="mb-6">
        GymPlix complies with Pinterest's developer policies and platform
        requirements.
      </p>

      {/* 6 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        6. Data Storage & Retention
      </h2>

      <ul className="list-disc pl-6 mb-6">
        <li>
          User data is stored securely using managed infrastructure (e.g.
          Supabase).
        </li>
        <li>
          Data is retained only while the user account remains active.
        </li>
        <li>
          Users may request deletion of their data at any time.
        </li>
        <li>
          Upon account deletion, associated data is permanently removed unless
          retention is required by law.
        </li>
      </ul>

      {/* 7 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        7. Data Security
      </h2>

      <p className="mb-2">
        We implement appropriate technical and organizational measures to protect
        your data, including:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>Encrypted connections (HTTPS)</li>
        <li>Authentication and access controls</li>
        <li>Restricted internal access to user data</li>
        <li>Secure cloud infrastructure</li>
        <li>Regular security assessments and updates</li>
      </ul>

      <p className="mb-6">
        No system is 100% secure, but we continuously work to protect your
        information.
      </p>

      {/* 8 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        8. User Rights
      </h2>

      <p className="mb-2">
        Depending on your location, you have the right to:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li><strong>Access</strong> your personal data</li>
        <li><strong>Request correction</strong> of inaccurate data</li>
        <li><strong>Request deletion</strong> of your data</li>
        <li>Withdraw consent for third-party integrations</li>
        <li>Export your data (data portability)</li>
      </ul>

      <p className="mb-6">
        To exercise your rights, please contact us at{" "}
        <a href="mailto:support@gymplix.com" className="underline">
          support@gymplix.com
        </a>
        . We will respond within 30 days in accordance with applicable regulations.
      </p>

      {/* 9 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        9. Third-Party Services
      </h2>

      <p className="mb-2">
        GymPlix may rely on trusted third-party services, including:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>Hosting and cloud infrastructure providers</li>
        <li>Authentication and database services</li>
        <li>Official APIs (such as Meta Platforms (Facebook) and Pinterest)</li>
      </ul>

      <p className="mb-6">
        These providers are used strictly to operate the platform and comply with
        applicable data protection standards.
      </p>

      {/* 10 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        10. Cookies & Tracking
      </h2>

      <p className="mb-2">
        GymPlix may use essential cookies or similar technologies to:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>Maintain user sessions</li>
        <li>Improve platform performance</li>
        <li>Ensure security</li>
      </ul>

      <p className="mb-6">
        We do not use cookies for advertising or behavioral tracking.
      </p>

      {/* 11 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        11. Children's Privacy
      </h2>

      <p className="mb-6">
        GymPlix is not intended for use by individuals under the age of 13. We do
        not knowingly collect personal data from children.
      </p>

      {/* 12 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        12. Changes to This Policy
      </h2>

      <p className="mb-6">
        We may update this Privacy Policy periodically. Changes will be posted on
        this page with an updated revision date. Continued use of the platform
        constitutes acceptance of the updated policy.
      </p>

      {/* 13 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        13. Contact Information
      </h2>

      <p>
        For privacy-related questions or requests, please contact:
        <br />
        <strong>GymPlix</strong> — Operated by Creative Brains LTD
        <br />
        📧{" "}
        <a
          href="mailto:support@gymplix.com"
          className="underline"
        >
          support@gymplix.com
        </a>
      </p>

    
    </div>
          <Footer />

    </>
  );
}
